import React, { useState, useEffect } from 'react';
import axiosInstance from '../../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Search,
  Globe,
  FileText,
  Image,
  Save,
  RefreshCw,
  Eye,
  AlertCircle,
  CheckCircle,
  Info,
  Code,
  Share2
} from 'lucide-react';
import { toast } from 'sonner';
import LoadingSpinner from '../../components/LoadingSpinner';

export default function AdminSEOSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    // Global SEO
    site_title: '',
    site_description: '',
    site_keywords: '',
    og_image: '',
    twitter_handle: '',
    favicon_url: '',
    
    // Home page
    home_title: '',
    home_description: '',
    home_keywords: '',
    
    // Products page
    products_title: '',
    products_description: '',
    
    // Robots & Sitemap
    robots_txt: '',
    sitemap_enabled: true,
    
    // Structured Data
    organization_name: '',
    organization_logo: '',
    organization_phone: '',
    organization_email: '',
    organization_address: '',
    
    // Social Media
    facebook_url: '',
    instagram_url: '',
    twitter_url: '',
    youtube_url: '',
    
    // Analytics codes (for manual insertion if needed)
    head_scripts: '',
    body_scripts: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${API_URL}/api/admin/seo-settings`);
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to fetch SEO settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axiosInstance.put(`${API_URL}/api/admin/seo-settings`, settings);
      toast.success('SEO settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6 space-y-6" data-testid="seo-settings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Search className="w-7 h-7 text-purple-600" />
            SEO Settings
          </h1>
          <p className="text-gray-500 mt-1">Optimize your store for search engines and social media</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save All Settings
        </Button>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="bg-white border shadow-sm p-1 rounded-xl">
          <TabsTrigger value="general" className="rounded-lg px-4 py-2 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
            <Globe className="w-4 h-4 mr-2" />
            General
          </TabsTrigger>
          <TabsTrigger value="pages" className="rounded-lg px-4 py-2 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
            <FileText className="w-4 h-4 mr-2" />
            Pages
          </TabsTrigger>
          <TabsTrigger value="social" className="rounded-lg px-4 py-2 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
            <Share2 className="w-4 h-4 mr-2" />
            Social Media
          </TabsTrigger>
          <TabsTrigger value="advanced" className="rounded-lg px-4 py-2 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
            <Code className="w-4 h-4 mr-2" />
            Advanced
          </TabsTrigger>
        </TabsList>

        {/* General SEO */}
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Site-wide SEO Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Site Title</Label>
                  <Input
                    value={settings.site_title}
                    onChange={(e) => handleChange('site_title', e.target.value)}
                    placeholder="NammaHut - Authentic Indian Products"
                  />
                  <p className="text-xs text-gray-500">This appears in browser tabs and search results</p>
                </div>
                <div className="space-y-2">
                  <Label>Twitter Handle</Label>
                  <Input
                    value={settings.twitter_handle}
                    onChange={(e) => handleChange('twitter_handle', e.target.value)}
                    placeholder="@nammahut"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Site Description (Meta Description)</Label>
                <Textarea
                  value={settings.site_description}
                  onChange={(e) => handleChange('site_description', e.target.value)}
                  placeholder="Discover authentic Indian products from local artisans and vendors..."
                  rows={3}
                />
                <p className="text-xs text-gray-500">{settings.site_description?.length || 0}/160 characters recommended</p>
              </div>

              <div className="space-y-2">
                <Label>Site Keywords</Label>
                <Textarea
                  value={settings.site_keywords}
                  onChange={(e) => handleChange('site_keywords', e.target.value)}
                  placeholder="indian products, handmade, artisan, local vendors, authentic, traditional"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Default OG Image URL</Label>
                  <Input
                    value={settings.og_image}
                    onChange={(e) => handleChange('og_image', e.target.value)}
                    placeholder="https://nammahut.com/og-image.jpg"
                  />
                  <p className="text-xs text-gray-500">Recommended: 1200x630 pixels</p>
                </div>
                <div className="space-y-2">
                  <Label>Favicon URL</Label>
                  <Input
                    value={settings.favicon_url}
                    onChange={(e) => handleChange('favicon_url', e.target.value)}
                    placeholder="https://nammahut.com/favicon.ico"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Organization Schema */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="w-5 h-5" />
                Organization Details (Structured Data)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Organization Name</Label>
                  <Input
                    value={settings.organization_name}
                    onChange={(e) => handleChange('organization_name', e.target.value)}
                    placeholder="NammaHut Private Limited"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Organization Logo URL</Label>
                  <Input
                    value={settings.organization_logo}
                    onChange={(e) => handleChange('organization_logo', e.target.value)}
                    placeholder="https://nammahut.com/logo.png"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact Phone</Label>
                  <Input
                    value={settings.organization_phone}
                    onChange={(e) => handleChange('organization_phone', e.target.value)}
                    placeholder="+91 9876543210"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <Input
                    value={settings.organization_email}
                    onChange={(e) => handleChange('organization_email', e.target.value)}
                    placeholder="contact@nammahut.com"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Business Address</Label>
                <Textarea
                  value={settings.organization_address}
                  onChange={(e) => handleChange('organization_address', e.target.value)}
                  placeholder="123 Main Street, Chennai, Tamil Nadu, India - 600001"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Page-specific SEO */}
        <TabsContent value="pages" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Home Page</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={settings.home_title}
                  onChange={(e) => handleChange('home_title', e.target.value)}
                  placeholder="NammaHut - Shop Authentic Indian Products Online"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={settings.home_description}
                  onChange={(e) => handleChange('home_description', e.target.value)}
                  placeholder="Shop authentic Indian products from verified local vendors..."
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Keywords</Label>
                <Input
                  value={settings.home_keywords}
                  onChange={(e) => handleChange('home_keywords', e.target.value)}
                  placeholder="online shopping, indian products, local vendors"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Products Page</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={settings.products_title}
                  onChange={(e) => handleChange('products_title', e.target.value)}
                  placeholder="All Products - NammaHut"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={settings.products_description}
                  onChange={(e) => handleChange('products_description', e.target.value)}
                  placeholder="Browse our collection of authentic Indian products..."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Social Media */}
        <TabsContent value="social" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Share2 className="w-5 h-5" />
                Social Media Links
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Facebook Page URL</Label>
                  <Input
                    value={settings.facebook_url}
                    onChange={(e) => handleChange('facebook_url', e.target.value)}
                    placeholder="https://facebook.com/nammahut"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Instagram URL</Label>
                  <Input
                    value={settings.instagram_url}
                    onChange={(e) => handleChange('instagram_url', e.target.value)}
                    placeholder="https://instagram.com/nammahut"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Twitter URL</Label>
                  <Input
                    value={settings.twitter_url}
                    onChange={(e) => handleChange('twitter_url', e.target.value)}
                    placeholder="https://twitter.com/nammahut"
                  />
                </div>
                <div className="space-y-2">
                  <Label>YouTube Channel</Label>
                  <Input
                    value={settings.youtube_url}
                    onChange={(e) => handleChange('youtube_url', e.target.value)}
                    placeholder="https://youtube.com/@nammahut"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Advanced */}
        <TabsContent value="advanced" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="w-5 h-5" />
                Custom Scripts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Head Scripts (before &lt;/head&gt;)</Label>
                <Textarea
                  value={settings.head_scripts}
                  onChange={(e) => handleChange('head_scripts', e.target.value)}
                  placeholder="<!-- Add custom scripts here -->"
                  rows={4}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500">Use for additional analytics, chat widgets, etc.</p>
              </div>
              <div className="space-y-2">
                <Label>Body Scripts (before &lt;/body&gt;)</Label>
                <Textarea
                  value={settings.body_scripts}
                  onChange={(e) => handleChange('body_scripts', e.target.value)}
                  placeholder="<!-- Add custom scripts here -->"
                  rows={4}
                  className="font-mono text-sm"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Robots.txt Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={settings.robots_txt}
                onChange={(e) => handleChange('robots_txt', e.target.value)}
                placeholder={`User-agent: *\nAllow: /\nSitemap: https://nammahut.com/sitemap.xml`}
                rows={6}
                className="font-mono text-sm"
              />
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings.sitemap_enabled}
                  onChange={(e) => handleChange('sitemap_enabled', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <Label>Enable automatic sitemap generation</Label>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
